/**
 * LiveRideTracker.js - Real-time driver tracking with ETA
 * Shows live map with driver location and estimated arrival time
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, MapPin, Clock, Navigation, Phone, User, Car,
  RefreshCw, AlertCircle, CheckCircle, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../services/api';

// Fix Leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const createIcon = (color) => new L.Icon({
  iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Car icon for driver
const carIcon = new L.DivIcon({
  html: `<div style="background: linear-gradient(135deg, #D4AF37, #E6C965); width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 4px 12px rgba(0,0,0,0.3);">
    <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
      <path d="M5 11l1.5-4.5h11L19 11m-4 8v-2a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v2m14-2v-2a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2M5 11v6h14v-6"/>
    </svg>
  </div>`,
  className: 'driver-marker',
  iconSize: [36, 36],
  iconAnchor: [18, 18]
});

const pickupIcon = createIcon('green');
const dropoffIcon = createIcon('red');

// Component to recenter map when driver moves
const MapUpdater = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || map.getZoom(), { animate: true });
    }
  }, [center, zoom, map]);
  return null;
};

const LiveRideTracker = ({ ride, onClose }) => {
  const [driverLocation, setDriverLocation] = useState(null);
  const [eta, setEta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const etaIntervalRef = useRef(null);

  const rideId = ride?.id;
  const status = ride?.status || 'pending';
  
  // Parse locations
  const pickupLocation = ride?.pickup_location || {};
  const dropoffLocation = ride?.dropoff_location || {};
  
  const pickupCoords = pickupLocation.latitude && pickupLocation.longitude 
    ? [pickupLocation.latitude, pickupLocation.longitude]
    : null;
  const dropoffCoords = dropoffLocation.latitude && dropoffLocation.longitude
    ? [dropoffLocation.latitude, dropoffLocation.longitude]
    : null;

  // Fetch ETA
  const fetchEta = useCallback(async () => {
    if (!rideId) return;
    try {
      const response = await api.get(`/rides/${rideId}/eta`);
      if (response.data) {
        setEta(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch ETA:', err);
    }
  }, [rideId]);

  // Connect to WebSocket for live tracking
  useEffect(() => {
    if (!rideId) return;
    
    const connectWebSocket = async () => {
      try {
        // Get auth token
        const tokenRes = await fetch(`${window.location.origin}/api/auth/ws-token`, {
          credentials: 'include'
        });
        
        if (!tokenRes.ok) {
          setError('Authentication required');
          setLoading(false);
          return;
        }
        
        const { ws_token } = await tokenRes.json();
        
        // Connect to ride tracking WebSocket
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl = `${wsProtocol}//${window.location.host}/api/ws/ride-tracking/${rideId}?token=${ws_token}`;
        
        wsRef.current = new WebSocket(wsUrl);
        
        wsRef.current.onopen = () => {
          console.log('Connected to ride tracking');
          setLoading(false);
        };
        
        wsRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            if (data.type === 'initial_state' || data.type === 'driver_location') {
              const location = data.driver_location || data.location;
              if (location) {
                setDriverLocation({
                  lat: location.latitude || location.lat,
                  lng: location.longitude || location.lng
                });
              }
            }
            
            if (data.type === 'ride_status') {
              // Status changed - refresh ETA
              fetchEta();
            }
          } catch (e) {
            console.error('Failed to parse WS message:', e);
          }
        };
        
        wsRef.current.onerror = (err) => {
          console.error('WebSocket error:', err);
          setError('Connection lost');
        };
        
        wsRef.current.onclose = () => {
          console.log('WebSocket closed');
        };
        
      } catch (err) {
        console.error('Failed to connect:', err);
        setError('Failed to connect to tracking');
        setLoading(false);
      }
    };
    
    connectWebSocket();
    
    // Fetch ETA every 30 seconds
    fetchEta();
    etaIntervalRef.current = setInterval(fetchEta, 30000);
    
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (etaIntervalRef.current) clearInterval(etaIntervalRef.current);
    };
  }, [rideId, fetchEta]);

  // Map center - prioritize driver location, then pickup
  const mapCenter = driverLocation 
    ? [driverLocation.lat, driverLocation.lng]
    : pickupCoords || [35.7796, -78.6382]; // Default: Raleigh, NC

  // Route line
  const routePoints = [];
  if (driverLocation) routePoints.push([driverLocation.lat, driverLocation.lng]);
  if (status === 'en_route_pickup' && pickupCoords) {
    routePoints.push(pickupCoords);
  } else if (status === 'in_progress' && dropoffCoords) {
    routePoints.push(dropoffCoords);
  }

  // Status display
  const statusConfig = {
    driver_assigned: { label: 'Driver Assigned', color: 'text-blue-400', icon: User },
    en_route_pickup: { label: 'Driver En Route', color: 'text-yellow-400', icon: Car },
    arrived_pickup: { label: 'Driver Arrived', color: 'text-green-400', icon: CheckCircle },
    in_progress: { label: 'In Progress', color: 'text-purple-400', icon: Navigation },
    completed: { label: 'Completed', color: 'text-green-500', icon: CheckCircle },
  };
  
  const currentStatus = statusConfig[status] || { label: status, color: 'text-gray-400', icon: Clock };
  const StatusIcon = currentStatus.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex flex-col"
    >
      {/* Header */}
      <div className="bg-[#111] border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#D4AF37]/20 rounded-lg">
            <Car className="w-5 h-5 text-[#D4AF37]" />
          </div>
          <div>
            <h2 className="text-white font-semibold">Live Tracking</h2>
            <p className="text-xs text-white/50">#{ride?.reference_number || rideId?.slice(0, 8)}</p>
          </div>
        </div>
        <button 
          onClick={onClose}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-white/60" />
        </button>
      </div>

      {/* Map */}
      <div className="flex-1 relative">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-[#0a0a0a]">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
          </div>
        ) : error ? (
          <div className="absolute inset-0 flex items-center justify-center bg-[#0a0a0a]">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-2" />
              <p className="text-white/60">{error}</p>
              <Button onClick={() => window.location.reload()} className="mt-4">
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </div>
          </div>
        ) : (
          <MapContainer
            center={mapCenter}
            zoom={14}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            
            <MapUpdater center={mapCenter} />
            
            {/* Driver marker */}
            {driverLocation && (
              <Marker position={[driverLocation.lat, driverLocation.lng]} icon={carIcon} />
            )}
            
            {/* Pickup marker */}
            {pickupCoords && (
              <Marker position={pickupCoords} icon={pickupIcon} />
            )}
            
            {/* Dropoff marker */}
            {dropoffCoords && (
              <Marker position={dropoffCoords} icon={dropoffIcon} />
            )}
            
            {/* Route line */}
            {routePoints.length >= 2 && (
              <Polyline 
                positions={routePoints}
                color="#D4AF37"
                weight={4}
                dashArray="10, 10"
              />
            )}
          </MapContainer>
        )}

        {/* ETA Overlay */}
        {eta && eta.eta_minutes && (
          <div className="absolute top-4 left-4 right-4 z-[1000]">
            <div className="bg-[#111]/90 backdrop-blur-xl rounded-xl p-4 border border-[#D4AF37]/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm">Estimated Arrival</p>
                  <p className="text-3xl font-bold text-[#D4AF37]">{eta.eta_minutes} min</p>
                </div>
                <div className="text-right">
                  <p className="text-white/60 text-sm">Distance</p>
                  <p className="text-xl font-semibold text-white">{eta.distance_miles} mi</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Info Panel */}
      <div className="bg-[#111] border-t border-white/10 p-4 space-y-4">
        {/* Status */}
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg bg-white/5`}>
            <StatusIcon className={`w-5 h-5 ${currentStatus.color}`} />
          </div>
          <div className="flex-1">
            <p className={`font-medium ${currentStatus.color}`}>{currentStatus.label}</p>
            <p className="text-xs text-white/50">
              {status === 'en_route_pickup' ? 'Driver is on the way to pick you up' :
               status === 'arrived_pickup' ? 'Driver has arrived at pickup location' :
               status === 'in_progress' ? 'You are on your way to destination' :
               'Waiting for driver'}
            </p>
          </div>
        </div>

        {/* Driver Info */}
        {ride?.driver_name && (
          <div className="flex items-center gap-3 p-3 bg-[#0a0a0a] rounded-xl">
            <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-[#E6C965] rounded-full flex items-center justify-center text-black font-bold">
              {ride.driver_name.charAt(0)}
            </div>
            <div className="flex-1">
              <p className="text-white font-medium">{ride.driver_name}</p>
              <p className="text-sm text-white/50">{ride.vehicle_type || 'Sedan'} • {ride.vehicle_color || ''} {ride.vehicle_make || ''}</p>
            </div>
            {ride.driver_phone && (
              <a 
                href={`tel:${ride.driver_phone}`}
                className="p-3 bg-emerald-500/20 hover:bg-emerald-500/30 rounded-xl transition-colors"
              >
                <Phone className="w-5 h-5 text-emerald-400" />
              </a>
            )}
          </div>
        )}

        {/* Locations */}
        <div className="space-y-2">
          <div className="flex items-center gap-3 text-sm">
            <div className="w-6 h-6 bg-emerald-500/20 rounded-full flex items-center justify-center">
              <MapPin className="w-3 h-3 text-emerald-400" />
            </div>
            <span className="text-white/70 truncate">{pickupLocation.address || 'Pickup location'}</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center">
              <Navigation className="w-3 h-3 text-red-400" />
            </div>
            <span className="text-white/70 truncate">{dropoffLocation.address || 'Dropoff location'}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default LiveRideTracker;
